import React, { useEffect, useState } from "react";
import {
  fetchUserExamSessions,
  fetchExamQuestionsCount,
  fetchTotalAnswered,
  fetchReviewProgress,
} from "../utils/api";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import styles from "./YourCourses.module.css";

const YourCourses = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Local state declarations
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const [error, setError] = useState(null);

  // For normal in-progress attempts (first attempt)
  const [answeredCounts, setAnsweredCounts] = useState({});
  const [questionCounts, setQuestionCounts] = useState({});

  // For Review mode attempts (second attempt)
  const [reviewProgress, setReviewProgress] = useState({});

  // Control for expanding/collapsing sections
  const [expanded, setExpanded] = useState({
    toTake: false,
    inProgress: false,
    completed: false,
  });

  // Delay loading indicator
  useEffect(() => {
    const timer = setTimeout(() => setShowLoading(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Load exam data
  useEffect(() => {
    const loadAllData = async () => {
      if (!user?.userId) {
        setError("User not logged in.");
        setLoading(false);
        return;
      }
      try {
        const fetchedExams = await fetchUserExamSessions(user.userId, token);
        const formattedExams = fetchedExams.map(exam => ({
          ...exam,
          selected_courses: typeof exam.selected_courses === "string"
            ? JSON.parse(exam.selected_courses)
            : exam.selected_courses || [],
        }));
        setExams(formattedExams);

        const firstAttemptAnswered = {};
        const firstAttemptQuestions = {};
        const reviewData = {};

        for (const exam of formattedExams) {
          if (exam.status === "in_progress") {
            if (exam.mode !== "Review") {
              let totalQuestions = 0;
              for (const courseId of exam.selected_courses) {
                const count = await fetchExamQuestionsCount(exam.examid, courseId, token);
                totalQuestions += count;
              }
              firstAttemptQuestions[exam.id] = totalQuestions;
              firstAttemptAnswered[exam.id] = await fetchTotalAnswered(exam.id, token);
            } else {
              const { secondAnswered, totalInReview } = await fetchReviewProgress(exam.id, token);
              reviewData[exam.id] = { secondAnswered, totalInReview };
            }
          }
        }

        setAnsweredCounts(firstAttemptAnswered);
        setQuestionCounts(firstAttemptQuestions);
        setReviewProgress(reviewData);
      } catch (err) {
        console.error("Error fetching exams:", err);
        setError("Failed to load exams.");
      } finally {
        setLoading(false);
      }
    };
    loadAllData();
  }, [user, token]);

  const examsToTake = exams.filter(e => e.status === "not_started");
  const examsInProgress = exams.filter(e => e.status === "in_progress");
  const completedExams = exams.filter(e => e.status === "completed");

  const toggleSection = section => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleTakeExam = exam => navigate(
    `/dashboard/exam/${exam.examid}`,
    { state: { sessionId: exam.id, selectedCourses: exam.selected_courses, examName: exam.exam_name, examId: exam.examid } }
  );

  const handleResumeExam = exam => {
    const isReview = exam.mode === "Review";
    navigate(
      `/dashboard/exam/${exam.examid}`,
      { state: { sessionId: exam.id, selectedCourses: exam.selected_courses, examName: exam.exam_name, examId: exam.examid, isResuming: true, isReviewResume: isReview } }
    );
  };

  const handleViewCompletedExam = exam => navigate(
    `/dashboard/completed-exams`,
    { state: { sessionId: exam.id, userId: user.userId } }
  );

  return (
    <div className={styles['user-list-container']}>
      <h2>Your Assigned Exams</h2>
      {loading && showLoading ? (
        <p className={styles['loading-message']}>Loading...</p>
      ) : error ? (
        <p className={styles['error-message']}>{error}</p>
      ) : exams.length === 0 ? (
        <p>No exams assigned to you.</p>
      ) : (
        <>
          <div className={styles['userlist-table-wrapper']}>
            <h3 onClick={() => toggleSection('toTake')} className={styles['section-header']}>
              Exams to Take ({examsToTake.length}) {expanded.toTake ? '▲' : '▼'}
            </h3>
            {expanded.toTake && (
              <table className={styles['user-table']}>
                <thead>
                  <tr>
                    <th>Exam Name</th>
                    <th>Selected Courses</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {examsToTake.length > 0 ? examsToTake.map(exam => (
                    <tr key={exam.id}>
                      <td>{exam.exam_name}</td>
                      <td>{exam.selected_courses.join(', ') || 'None'}</td>
                      <td>
                        <button className={styles['start-btn']} onClick={() => handleTakeExam(exam)}>
                          Start Exam
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="3">No exams to take.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          <div className={styles['userlist-table-wrapper']}>
            <h3 onClick={() => toggleSection('inProgress')} className={styles['section-header']}>
              In Progress ({examsInProgress.length}) {expanded.inProgress ? '▲' : '▼'}
            </h3>
            {expanded.inProgress && (
              <table className={styles['user-table']}>
                <thead>
                  <tr>
                    <th>Exam Name</th>
                    <th>Selected Courses</th>
                    <th>Mode</th>
                    <th>Progress</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {examsInProgress.length > 0 ? examsInProgress.map(exam => {
                    if (exam.mode !== 'Review') {
                      return (
                        <tr key={exam.id}>
                          <td>{exam.exam_name}</td>
                          <td>{exam.selected_courses.join(', ') || 'None'}</td>
                          <td>{exam.mode}</td>
                          <td>{answeredCounts[exam.id] ?? 0} / {questionCounts[exam.id] ?? 0}</td>
                          <td>
                            <button className={styles['reset-btn']} onClick={() => handleResumeExam(exam)}>
                              Resume Exam
                            </button>
                          </td>
                        </tr>
                      );
                    } else {
                      const { secondAnswered = 0, totalInReview = 0 } = reviewProgress[exam.id] || {};
                      return (
                        <tr key={exam.id}>
                          <td>{exam.exam_name}</td>
                          <td>{exam.selected_courses.join(', ') || 'None'}</td>
                          <td>{exam.mode}</td>
                          <td>{secondAnswered} / {totalInReview}</td>
                          <td>
                            <button className={styles['reset-btn']} onClick={() => handleResumeExam(exam)}>
                              Continue Review
                            </button>
                          </td>
                        </tr>
                      );
                    }
                  }) : (
                    <tr><td colSpan="5">No exams in progress.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          <div className={styles['userlist-table-wrapper']}>
            <h3 onClick={() => toggleSection('completed')} className={styles['section-header']}>
              Completed ({completedExams.length}) {expanded.completed ? '▲' : '▼'}
            </h3>
            {expanded.completed && (
              <table className={styles['user-table']}>
                <thead>
                  <tr>
                    <th>Exam Name</th>
                    <th>Selected Courses</th>
                    <th>Score</th>
                    <th>Date Completed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {completedExams.length > 0 ? completedExams.map(exam => (
                    <tr key={exam.id}>
                      <td>{exam.exam_name}</td>
                      <td>{exam.selected_courses.join(', ') || 'None'}</td>
                      <td>{exam.score}%</td>
                      <td>{new Date(exam.end_time).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</td>
                      <td>
                        <button className={styles['reset-btn']} onClick={() => handleViewCompletedExam(exam)}>
                          View Results
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5">No completed exams.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default YourCourses;
